// heartbeat.ts
import {
  postJsMessage,
  registerSpecificNotificationHandler,
  unregisterSpecificNotificationHandler,
  type Logger,
  getLogger,
} from '../../bridge';
import { isAvailable } from 'jssdk/utils';
import { NativeAPIResponseMessage } from '@zoom/hybrid-jsbridge';

/* ----------  Types  ---------- */
export type HeartbeatStatus = {
  state: HeartbeatState;
  retryCount: number;
  maxRetryTimes: number;
  reason: 'js2native' | 'native2js' | undefined;
};

export type HeartbeatConfig = {
  onTimeout?: (reason: 'js2native' | 'native2js') => void;
  onResume?: (reason: 'js2native' | 'native2js') => void;
  onHeartbeat?: (status: HeartbeatStatus) => void;
};

type NativeHeartbeatPayload = {
  notification: 'UnifyNotify_NativeHeartbeat';
  heartbeatInterval: string; // seconds, kept for extension
  timeout: string; // seconds
  maxRetryTimes: string;
};

/* ----------  State Machine  ---------- */
enum HeartbeatState {
  Disabled = 'DISABLED',
  Idle = 'IDLE',
  Waiting = 'WAITING',
  Timeout = 'TIMEOUT',
}

/* ----------  Heartbeat Manager  ---------- */
class HeartbeatManager {
  private state = HeartbeatState.Disabled;
  private timeoutReason?: 'js2native' | 'native2js' = undefined;
  private config: Required<HeartbeatConfig> = {
    onTimeout: () => {},
    onResume: () => {},
    onHeartbeat: () => {},
  };
  private handlerRef = this.onNativeHeartbeat.bind(this);
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatIntervalMs: number = 0;
  private logger: Logger | null = null;

  /* ---- public ---- */
  enable(cfg: HeartbeatConfig = {}) {
    this.logger = getLogger();
    this.logger.info('heartbeat enable');
    this.config = { ...this.config, ...cfg };
    this.state = HeartbeatState.Idle;
    registerSpecificNotificationHandler(
      ['UnifyNotify_NativeHeartbeat'],
      this.handlerRef
    );
    this.resetIdleTimer();
  }

  disable() {
    this.logger?.info('Heartbeat disable');
    this.state = HeartbeatState.Disabled;
    unregisterSpecificNotificationHandler(
      ['UnifyNotify_NativeHeartbeat'],
      this.handlerRef
    );
    this.clearIdleTimer();
    this.timeoutReason = undefined;
    this.heartbeatIntervalMs = 0;
    this.retryCount = 0;
  }

  getStatus(): HeartbeatStatus {
    return {
      state: this.state,
      retryCount: this.retryCount,
      maxRetryTimes: this.maxRetryTimes,
      reason: this.timeoutReason,
    };
  }

  /* ---- private ---- */
  private timeoutS = 0;
  private maxRetryTimes = 0;
  private retryCount = 0;

  private resetIdleTimer() {
    this.clearIdleTimer();
    if (
      this.state === HeartbeatState.Disabled ||
      this.heartbeatIntervalMs === 0
    )
      return;

    this.logger?.info(`start timer for native heartbeat at ${Date.now()}`);
    this.idleTimer = setTimeout(() => {
      if (this.state === HeartbeatState.Disabled) return;

      this.timeoutReason = 'native2js';
      this.state = HeartbeatState.Timeout;
      this.config.onTimeout(this.timeoutReason);
      this.logger?.info(`native heartbeat timeout at ${Date.now()}`);
    }, this.heartbeatIntervalMs * 1.2); // The actual timer may have a slight deviation, so we multiply by 1.2 to ensure it stays within the margin of error as much as possible.
  }

  private clearIdleTimer() {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }

  private async onNativeHeartbeat(
    data: NativeAPIResponseMessage<NativeHeartbeatPayload>
  ) {
    if (this.state === HeartbeatState.Disabled) return;

    this.logger?.info(`onNativeHeartbeat at ${Date.now()}`, data);
    const { heartbeatInterval, timeout, maxRetryTimes } = data.result;

    if (this.timeoutReason === 'native2js') {
      this.config.onResume('native2js');
      this.timeoutReason = undefined;
    }
    this.clearIdleTimer();
    this.state = HeartbeatState.Idle;
    this.timeoutS = parseInt(String(timeout));
    this.maxRetryTimes = parseInt(String(maxRetryTimes));
    this.retryCount = 0;
    this.heartbeatIntervalMs = parseInt(String(heartbeatInterval)) * 1000;

    this.config.onHeartbeat(this.getStatus());
    this.resetIdleTimer();
    await this.sendHeartbeatLoop();
  }

  private async sendHeartbeatLoop() {
    if (this.state === HeartbeatState.Disabled) return;

    this.retryCount += 1;
    this.state = HeartbeatState.Waiting;

    try {
      this.logger?.info('send js heartbeat');
      await postJsMessage(
        'webview.heartbeat',
        {},
        { timeout: parseInt(String(this.timeoutS)) }
      );

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      if (this.state === HeartbeatState.Disabled) return;

      if (this.timeoutReason === 'js2native') {
        this.config.onResume('js2native');
        this.timeoutReason = undefined;
      }
      this.state = HeartbeatState.Idle;
      this.logger?.info('js send webview.heartbeat successfully, ack received');
      this.retryCount = 0;
    } catch {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      if (this.state === HeartbeatState.Disabled) return;

      if (this.retryCount >= this.maxRetryTimes) {
        this.timeoutReason = 'js2native';
        this.state = HeartbeatState.Timeout;
        this.config.onTimeout(this.timeoutReason);
        this.logger?.info(
          `js send webview.heartbeat timeout, retry failed. invoke onTimeout callback`
        );
      } else {
        // immediate retry; postJsMessage will enforce timeout
        this.logger?.info(
          `js send webview.heartbeat timeout, retry ${this.retryCount}/${this.maxRetryTimes}`
        );
        await this.sendHeartbeatLoop();
      }
    }
  }
}

/* ----------  Singleton & Public API  ---------- */
let manager: HeartbeatManager | null = null;

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.enableHeartbeat
 *
 * [PLATFORM]
 * mac        NotSupport
 * windows    NotSupport
 * linux      NotSupport
 * ios        6.6.0
 * android    6.6.0
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export const enableHeartbeat = async (config?: HeartbeatConfig) => {
  await isAvailable('webview.enableHeartbeat');
  if (!manager) manager = new HeartbeatManager();
  manager.enable(config);
};

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.disableHeartbeat
 *
 * [PLATFORM]
 * mac        NotSupport
 * windows    NotSupport
 * linux      NotSupport
 * ios        6.6.0
 * android    6.6.0
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export const disableHeartbeat = async () => {
  await isAvailable('webview.disableHeartbeat');
  if (manager) {
    manager.disable();
    manager = null;
  }
};

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.getHeartbeatStatus
 *
 * [PLATFORM]
 * mac        NotSupport
 * windows    NotSupport
 * linux      NotSupport
 * ios        6.6.0
 * android    6.6.0
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export const getHeartbeatStatus = async (): Promise<HeartbeatStatus | null> => {
  await isAvailable('webview.getHeartbeatStatus');
  return manager ? manager.getStatus() : null;
};
